import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Input } from '../components/ui/input.js';
import { Card, CardBody } from '../components/ui/card.js';
import { Pill, type PillVariant } from '../components/ui/pill.js';
import { Feedback } from '../components/ui/feedback.js';
import { ApiError, api, type SalesOrderSummary } from '../lib/api.js';
import { cn } from '../lib/cn.js';

interface SalesOrdersSearchParams {
  status?: string;
  type?: string;
  customerNo?: string;
  orderNo?: string;
}

/**
 * View buttons map to (status, type) filter pairs. Spire stores those
 * independently — a quote is type='Q' regardless of status, an open order
 * is status='O' type='W'.
 */
const VIEWS: ReadonlyArray<{ id: string; label: string; filters: { status?: string; type?: string } }> = [
  { id: 'open', label: 'Open', filters: { status: 'O', type: 'W' } },
  { id: 'quotes', label: 'Quotes', filters: { type: 'Q' } },
  { id: 'invoiced', label: 'Invoiced', filters: { status: 'P' } },
];

function matchView(search: SalesOrdersSearchParams): string {
  for (const view of VIEWS) {
    if (view.filters.status === search.status && view.filters.type === search.type) {
      return view.id;
    }
  }
  return 'open';
}

export function SalesOrdersSearchScreen() {
  const navigate = useNavigate({ from: '/sales-orders' });
  const search = useSearch({ from: '/sales-orders' }) as SalesOrdersSearchParams;
  const customerNo = search.customerNo ?? '';
  const orderNo = search.orderNo ?? '';
  const currentView = matchView(search);

  const [customerNoDraft, setCustomerNoDraft] = useState(customerNo);
  const [orderNoDraft, setOrderNoDraft] = useState(orderNo);

  useEffect(() => {
    if (customerNoDraft === customerNo && orderNoDraft === orderNo) return;
    const id = setTimeout(() => {
      void navigate({
        search: {
          status: search.status,
          type: search.type,
          customerNo: customerNoDraft || undefined,
          orderNo: orderNoDraft || undefined,
        },
      });
    }, 250);
    return () => clearTimeout(id);
  }, [customerNoDraft, orderNoDraft, customerNo, orderNo, search.status, search.type, navigate]);

  const query = useQuery({
    queryKey: ['sales-orders', 'search', search.status, search.type, customerNo, orderNo],
    queryFn: ({ signal }) =>
      api.salesOrders.search(
        {
          status: search.status,
          type: search.type,
          customerNo: customerNo || undefined,
          orderNo: orderNo || undefined,
          limit: 30,
        },
        signal,
      ),
    enabled: Boolean(search.status || search.type),
    placeholderData: keepPreviousData,
  });

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold tracking-tight text-ink">Sales orders</h1>

      <div className="flex flex-wrap gap-1.5">
        {VIEWS.map((view) => {
          const active = currentView === view.id;
          return (
            <button
              key={view.id}
              type="button"
              onClick={() =>
                navigate({
                  search: {
                    status: view.filters.status,
                    type: view.filters.type,
                    customerNo: customerNoDraft || undefined,
                    orderNo: orderNoDraft || undefined,
                  },
                })
              }
              aria-pressed={active}
              className={cn(
                'rounded-full border px-3 py-1 text-xs transition-colors',
                active
                  ? 'bg-brand-50 border-brand-200 text-ink-accent font-medium'
                  : 'bg-surface border-line-subtle text-ink-muted hover:text-ink',
              )}
            >
              {view.label}
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Input
          aria-label="Customer number"
          placeholder="Customer #"
          value={customerNoDraft}
          onChange={(e) => setCustomerNoDraft(e.target.value.trim().toUpperCase())}
          className="font-mono"
          autoCapitalize="characters"
          autoCorrect="off"
        />
        <Input
          aria-label="Order number"
          placeholder="Order # contains…"
          value={orderNoDraft}
          onChange={(e) => setOrderNoDraft(e.target.value.trim())}
          className="font-mono"
        />
      </div>

      <Results query={query} viewId={currentView} />
    </div>
  );
}

interface ResultsProps {
  query: ReturnType<typeof useQuery<SalesOrderSummary[], ApiError>>;
  viewId: string;
}

function Results({ query, viewId }: ResultsProps) {
  if (query.isError) {
    return (
      <Feedback variant="error" size="lg">
        <div className="font-semibold">Failed to load orders.</div>
        <div className="text-xs mt-1 opacity-80">{query.error.message}</div>
      </Feedback>
    );
  }
  if (query.isPending) return <SkeletonList />;

  const orders = query.data ?? [];
  if (orders.length === 0) {
    return (
      <Card>
        <CardBody>
          <div className="text-sm text-ink-muted">No orders match the current {viewId} filter.</div>
        </CardBody>
      </Card>
    );
  }

  return (
    <ul className="space-y-2">
      {orders.map((o) => (
        <li key={o.spireId}>
          <OrderCard order={o} />
        </li>
      ))}
    </ul>
  );
}

function OrderCard({ order }: { order: SalesOrderSummary }) {
  const docTone = docPill(order);
  return (
    <Link
      to="/sales-orders/$id"
      params={{ id: String(order.spireId) }}
      className="block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-300 rounded-lg"
    >
      <Card className="transition-shadow hover:shadow-md active:shadow-sm">
        <CardBody className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-sm font-semibold text-ink">{order.orderNo}</span>
              <Pill variant={docTone.variant}>{docTone.label}</Pill>
              {order.hold && <Pill variant="danger">Hold</Pill>}
            </div>
            <div className="mt-0.5 text-sm text-ink truncate">{order.customer.name}</div>
            <div className="mt-0.5 text-xs text-ink-muted">
              {order.orderDate ?? '—'} · <span className="font-mono">{order.customer.customerNo}</span>
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-ink font-semibold tabular-nums">${order.total}</div>
            <div className="text-xs text-ink-muted">GP ${order.grossProfit}</div>
          </div>
        </CardBody>
      </Card>
    </Link>
  );
}

/**
 * Combined doc-badge resolution — quote (type) takes priority over open/invoiced
 * (status) for visual identity. An invoiced quote (rare) shows "invoiced".
 */
function docPill(o: SalesOrderSummary): { variant: PillVariant; label: string } {
  if (o.status === 'invoiced') return { variant: 'success', label: 'Invoiced' };
  if (o.status === 'cancelled') return { variant: 'neutral', label: 'Cancelled' };
  if (o.type === 'quote') return { variant: 'warning', label: 'Quote' };
  if (o.status === 'open') return { variant: 'info', label: 'Open' };
  return { variant: 'neutral', label: o.status };
}

function SkeletonList() {
  return (
    <ul className="space-y-2" aria-busy="true">
      {Array.from({ length: 4 }).map((_, i) => (
        <li key={i}>
          <Card>
            <CardBody className="flex items-start gap-3">
              <div className="flex-1 space-y-2">
                <div className="h-4 w-32 bg-surface-muted rounded animate-pulse" />
                <div className="h-3 w-3/4 bg-surface-muted rounded animate-pulse" />
              </div>
              <div className="h-4 w-16 bg-surface-muted rounded animate-pulse" />
            </CardBody>
          </Card>
        </li>
      ))}
    </ul>
  );
}
